import classNames from "classnames";
import * as React from "react";
import "./kuika-highlight-text.scss";

/**
 * A string with some of its characters marked as matching a search.
 *
 * The ranges come from the search that produced them, measured in characters of THIS string,
 * so the caller does the matching and this only draws it. That split is what keeps the
 * marking out of `innerHTML`: a label is user-authored text and a description arrives from
 * the backend, and building markup out of either by string replacement is both an injection
 * and a way to break the text it was meant to decorate.
 */

export interface KuikaHighlightRange {
  /** Inclusive start, exclusive end, in characters of `text`. */
  start: number;
  end: number;
}

export interface KuikaHighlightTextProps {
  text: string;
  /** Marked as matching. Overlapping or out-of-order ranges are sorted and clamped. */
  ranges?: readonly KuikaHighlightRange[];
  className?: string;
  /** Goes on the wrapper, since the marks inside it are not addressable individually. */
  dataTestId?: string;
}

interface ISegment {
  text: string;
  isMatch: boolean;
}

/** Split the text into runs, saying which of them matched. */
export const toHighlightSegments = (text: string, ranges: readonly KuikaHighlightRange[]): ISegment[] => {
  const sorted: KuikaHighlightRange[] = ranges
    .map((range: KuikaHighlightRange): KuikaHighlightRange => ({
      start: Math.max(0, Math.min(range.start, text.length)),
      end: Math.max(0, Math.min(range.end, text.length))
    }))
    .filter((range: KuikaHighlightRange): boolean => range.end > range.start)
    .sort((a: KuikaHighlightRange, b: KuikaHighlightRange): number => a.start - b.start);

  const segments: ISegment[] = [];
  let at: number = 0;
  sorted.forEach((range: KuikaHighlightRange): void => {
    // A range starting inside the previous one adds nothing: the characters are already marked.
    if (range.start < at) {
      if (range.end <= at) return;
      segments.push({ text: text.slice(at, range.end), isMatch: true });
      at = range.end;
      return;
    }
    if (range.start > at) segments.push({ text: text.slice(at, range.start), isMatch: false });
    segments.push({ text: text.slice(range.start, range.end), isMatch: true });
    at = range.end;
  });
  if (at < text.length) segments.push({ text: text.slice(at), isMatch: false });
  return segments;
};

const KuikaHighlightText: React.FC<KuikaHighlightTextProps> = ({ text, ranges, className, dataTestId }) => {
  if (!ranges || ranges.length === 0) {
    return (
      <span className={className} data-testid={dataTestId}>
        {text}
      </span>
    );
  }

  return (
    <span className={classNames("kuika-highlight-text", className)} data-testid={dataTestId}>
      {toHighlightSegments(text, ranges).map((segment: ISegment, index: number) =>
        segment.isMatch ? (
          // eslint-disable-next-line react/no-array-index-key
          <mark className="kuika-highlight-text__mark" key={`${index}-${segment.text}`}>
            {segment.text}
          </mark>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={`${index}-${segment.text}`}>{segment.text}</React.Fragment>
        )
      )}
    </span>
  );
};

export default KuikaHighlightText;
